/**
 * Dashboard Page for trAInedup.ai
 * Main dashboard interface for authenticated users
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  TrendingUp,
  Award,
  ArrowRight,
  Play,
  CheckCircle,
  Loader2,
  Zap,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/common/Card';
import { Button, ButtonLink } from '../components/common/Button';
import { ROUTES } from '../utils/constants';
import { learningPlansService, LikedCourse } from '../services/firebase/learningPlans';
import { courseService } from '../services/firebase/courses';
import { useGamification } from '../hooks/useGamification';
import { DEFAULT_TASKS } from '../types/gamification';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { SubscriptionTier } from '../types';
import { SkillsAssessmentWidget } from '../components/dashboard/SkillsAssessmentWidget';
import { CreditsWidget } from '../components/dashboard/CreditsWidget';
import { SUBSCRIPTION_TIERS } from '../services/stripe/config';

/**
 * Dashboard page component
 * Displays user progress, recent activity, and quick actions
 */
export const DashboardPage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { progress: gamificationProgress } = useGamification();

  // Check if user is on 12-month package (hide skills assessment)
  const isTwelveMonthUser = user?.subscriptionTier === SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE;

  // State for real data
  const [learningPlanCourses, setLearningPlanCourses] = useState<LikedCourse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    stats: dashboardStats,
    loading: statsLoading,
    error: statsError,
    refresh: refreshDashboardStats,
  } = useDashboardStats({ autoRefresh: false });

  // Always refresh user data on mount to get latest credits/subscription
  useEffect(() => {
    refreshUser && refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      setError(null);

      // Create a timeout promise to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Dashboard loading timed out')), 15000);
      });

      const dataPromise = Promise.all([
        refreshDashboardStats(),
        learningPlansService.getLearningPlanCourses(),
      ]);

      const result = await Promise.race([dataPromise, timeoutPromise]) as [void, LikedCourse[]];
      const [, courses] = result;

      setLearningPlanCourses(courses);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data. Please try refreshing the page.');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, user?.currentLearningPlanId, refreshDashboardStats]); // Re-fetch when currentLearningPlanId changes

  useEffect(() => {
    if (!user) {
      setLearningPlanCourses([]);
      setIsLoading(false);
      return;
    }

    loadDashboardData();
  }, [user, loadDashboardData]);

  // Format time duration from total seconds tracked in Firestore
  const formatDuration = (seconds: number) => {
    if (!seconds) {
      return '0m';
    }

    const totalMinutes = Math.floor(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
    }

    return `${mins}m`;
  };

  // Get progress percentage for a course
  const getCourseProgress = (course: LikedCourse) => {
    return course.progress || 0;
  };

  const courseCardClassName =
    'flex items-center justify-between p-4 border border-neutral-200 rounded-lg hover:border-primary-300 transition-colors duration-200';

  // Courses that are actually in progress (user has clicked "Start")
  // A course is in progress if startedAt exists, even if progress is 0%
  // This matches the learning journey logic
  const inProgressCourses = useMemo(() => {
    return learningPlanCourses.filter(course => {
      const progress = getCourseProgress(course);
      const isCompleted = Boolean(course.completedAt) || course.status === 'completed' || progress >= 100;
      
      // A course is started if the user clicked "Start" (startedAt exists) or has any progress
      const isStarted = Boolean(course.startedAt) || progress > 0;
      
      return isStarted && !isCompleted;
    });
  }, [learningPlanCourses]);

  // All courses that can be continued (in progress + ready to start)
  const continueLearningCourses = useMemo(() => {
    return learningPlanCourses.filter(course => {
      const progress = getCourseProgress(course);
      const isCompleted = Boolean(course.completedAt) || course.status === 'completed' || progress >= 100;
      return !isCompleted;
    });
  }, [learningPlanCourses]);

  const hasCompletedAssessment = useMemo(() => {
    if (user?.skillsAssessmentCompleted) {
      return true;
    }

    return gamificationProgress?.completedTasks.includes('take_skills_assessment') ?? false;
  }, [user?.skillsAssessmentCompleted, gamificationProgress?.completedTasks]);

  const isPaidUser = user?.subscriptionTier ? user.subscriptionTier !== SubscriptionTier.FREE : false;

  const renderUnlockLearningPlanButton = () => {
    if (isPaidUser) {
      return null;
    }

    if (hasCompletedAssessment) {
      return (
        <ButtonLink
          href="https://trainedup.ai/pricing"
          variant="outline"
          className="border-white text-white hover:bg-white hover:text-primary w-full sm:w-auto text-center"
        >
          Unlock Learning Plan
        </ButtonLink>
      );
    }

    return (
      <Link to={ROUTES.skillsAssessment} className="w-full sm:w-auto">
        <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary w-full sm:w-auto">
          Unlock Learning Plan
        </Button>
      </Link>
    );
  };

  const handleStreakClick = async () => {
    if (inProgressCourses.length > 0) {
      // Sort by lastAccessedAt descending to get most recently accessed in-progress course
      const sortedCourses = [...inProgressCourses].sort((a, b) => {
        const timeA = a.lastAccessedAt?.toMillis() || 0;
        const timeB = b.lastAccessedAt?.toMillis() || 0;
        return timeB - timeA;
      });

      const mostRecent = sortedCourses[0];
      try {
        const firstUncompleted = await courseService.getFirstUncompletedModule(mostRecent.courseId);
        if (firstUncompleted) {
          navigate(`/courses/${mostRecent.courseId}/modules/${firstUncompleted.id}`);
        } else {
          navigate(`/courses/${mostRecent.courseId}`);
        }
      } catch (error) {
        console.error("Error finding next module:", error);
        navigate(ROUTES.courses);
      }
    } else if (continueLearningCourses.length > 0) {
      // If no in-progress courses, go to first course to start
      navigate(`/courses/${continueLearningCourses[0].courseId}`);
    } else {
      navigate(ROUTES.courses);
    }
  };

  // Show loading state
  if (isLoading || statsLoading) {
    return (
      <div className="p-6 space-y-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-neutral-600">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || statsError) {
    return (
      <div className="p-6 space-y-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || statsError}</p>
            <Button onClick={loadDashboardData} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 sm:space-y-8 max-w-full overflow-x-hidden">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary to-secondary rounded-lg p-4 sm:p-6 text-white max-w-full overflow-hidden">
        <h1 className="font-montserrat font-bold text-xl sm:text-2xl mb-2">
          Welcome back, {user?.displayName?.split(' ')[0] || 'there'}! 👋
        </h1>
        <p className="text-base sm:text-lg opacity-90 mb-4">
          Ready to continue your AI learning journey? You're making great progress!
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to={ROUTES.courses} className="w-full sm:w-auto">
            <Button variant="primary" icon={BookOpen} iconPosition="right" className="w-full sm:w-auto">
              Continue Learning
            </Button>
          </Link>
          {!isTwelveMonthUser && (
            <Link to={ROUTES.learningPlan} className="w-full sm:w-auto">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary w-full sm:w-auto">
                View Learning Plan
              </Button>
            </Link>
          )}
          {renderUnlockLearningPlanButton()}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link
          to={ROUTES.dashboardCoursesInProgress}
          className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-lg"
        >
          <Card hover className="h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-neutral-600">Courses in Progress</p>
                <p className="text-xl sm:text-2xl font-bold text-neutral-800">
                  {dashboardStats?.coursesInProgress || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
            </div>
          </Card>
        </Link>

        <Link
          to={ROUTES.dashboardCoursesCompleted}
          className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-lg"
        >
          <Card hover className="h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-neutral-600">Completed Courses</p>
                <p className="text-xl sm:text-2xl font-bold text-neutral-800">
                  {dashboardStats?.coursesCompleted || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                <Award className="w-6 h-6 text-secondary" />
              </div>
            </div>
          </Card>
        </Link>

        <Link
          to={ROUTES.aiReadiness}
          className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-lg"
        >
          <Card hover className="h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-neutral-600">XP</p>
                <p className="text-xl sm:text-2xl font-bold text-neutral-800">
                  {(gamificationProgress?.totalXP || 0).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-accent" />
              </div>
            </div>
          </Card>
        </Link>

        <div 
          onClick={handleStreakClick}
          className="cursor-pointer block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-lg"
        >
          <Card hover className="h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-neutral-600">Current Streak</p>
                <p className="text-xl sm:text-2xl font-bold text-neutral-800">
                  {dashboardStats?.currentStreak || 0} days
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 items-start lg:items-stretch gap-6 max-w-full">
        {/* Recent Courses */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-montserrat font-semibold text-lg sm:text-xl text-neutral-800">
                {inProgressCourses.length > 0 ? 'In Progress' : 'Continue Learning'}
              </h2>
              <Link to={inProgressCourses.length > 0 ? ROUTES.learningJourney : ROUTES.courses}>
                <Button variant="ghost" size="sm" icon={ArrowRight} iconPosition="right">
                  View All
                </Button>
              </Link>
            </div>

            <div className="space-y-4">
              {inProgressCourses.length > 0 ? (
                // Show in-progress courses (prioritized)
                inProgressCourses.slice(0, 3).map(course => (
                  <div key={course.courseId} className={courseCardClassName}>
                    <div className="flex-1">
                      <h3 className="font-medium text-neutral-800 mb-1">{course.courseTitle}</h3>
                      <p className="text-sm text-neutral-600 mb-2">
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          In Progress
                        </span>
                      </p>
                      
                      {/* Progress bar */}
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-neutral-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full h-2 transition-all duration-300"
                            style={{ width: `${getCourseProgress(course)}%` }}
                          />
                        </div>
                        <span className="text-sm text-neutral-600">{getCourseProgress(course)}%</span>
                      </div>
                      
                      <p className="text-xs text-neutral-500 mt-2">
                        {course.lastAccessedAt 
                          ? `Last active ${course.lastAccessedAt.toDate().toLocaleDateString()}`
                          : `Added ${course.addedAt.toDate().toLocaleDateString()}`}
                        {course.timeSpent > 0 && ` • ${formatDuration(course.timeSpent)} spent`}
                      </p>
                    </div>
                    
                    <Link to={`/courses/${course.courseId}`}>
                      <Button variant="primary" size="sm" icon={Play} className="ml-4">
                        Continue
                      </Button>
                    </Link>
                  </div>
                ))
              ) : continueLearningCourses.length > 0 ? (
                // Show ready to start courses if no in-progress courses
                continueLearningCourses.slice(0, 3).map(course => (
                  <div key={course.courseId} className={courseCardClassName}>
                    <div className="flex-1">
                      <h3 className="font-medium text-neutral-800 mb-1">{course.courseTitle}</h3>
                      <p className="text-sm text-neutral-600 mb-2">
                        {course.completedAt ? 'Completed' : course.startedAt ? 'In Progress' : 'Ready to Start'}
                      </p>
                      
                      {/* Progress bar */}
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-neutral-200 rounded-full h-2">
                          <div
                            className="bg-primary rounded-full h-2 transition-all duration-300"
                            style={{ width: `${getCourseProgress(course)}%` }}
                          />
                        </div>
                        <span className="text-sm text-neutral-600">{getCourseProgress(course)}%</span>
                      </div>
                      
                      <p className="text-xs text-neutral-500 mt-2">
                        Added {course.addedAt.toDate().toLocaleDateString()}
                        {course.timeSpent > 0 && ` • ${formatDuration(course.timeSpent)} spent`}
                      </p>
                    </div>
                    
                    <Link to={`/courses/${course.courseId}`}>
                      <Button variant="primary" size="sm" icon={Play} className="ml-4">
                        {course.startedAt ? 'Continue' : 'Start'}
                      </Button>
                    </Link>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-base sm:text-lg text-neutral-800 mb-2">
                    {learningPlanCourses.length > 0
                      ? 'Nice work! You have completed all courses in your plan'
                      : 'No courses in your learning plan yet'}
                  </h3>
                  <p className="text-neutral-600 mb-4">
                    {learningPlanCourses.length > 0
                      ? 'Add new courses to keep the momentum going.'
                      : isTwelveMonthUser
                        ? 'Browse our course catalog to get started.'
                        : 'Take a Skills Assessment or browse our course catalog to get started.'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {!isTwelveMonthUser && (
                      <Link to={ROUTES.skillsAssessment}>
                        <Button variant="primary" size="sm">
                          Take Skills Assessment
                        </Button>
                      </Link>
                    )}
                    <Link to={ROUTES.courses}>
                      <Button variant="outline" size="sm">
                        Browse Courses
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="flex flex-col gap-6 lg:h-full lg:justify-between">
          {/* Credits Widget for 12-month users, Skills Assessment for others */}
          {isTwelveMonthUser ? (
            <CreditsWidget />
          ) : (
            <SkillsAssessmentWidget lastAssessmentDate={user?.lastAssessmentDate} />
          )}

          {/* Quick Actions */}
          <Card>
            <h3 className="font-montserrat font-semibold text-base sm:text-lg text-neutral-800 mb-4">
              Quick Actions
            </h3>

            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                disabled
                className="flex flex-col items-center gap-1 text-neutral-600"
              >
                <span className="font-medium text-neutral-700">Book Expert Consultation</span>
                <span className="text-xs text-neutral-500">Premium tier only</span>
              </Button>

              <Link to="/community">
                <Button variant="outline" size="sm" fullWidth>
                  Join Community Discussion
                </Button>
              </Link>

              <Link to={ROUTES.community}>
                <Button variant="outline" size="sm" fullWidth>
                  Give Feedback
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>

      {/* AI Readiness Progress Banner */}
      {(() => {
        // Get the latest completed task
        const completedTaskIds = gamificationProgress?.completedTasks || [];
        const latestTaskId = completedTaskIds.length > 0 ? completedTaskIds[completedTaskIds.length - 1] : null;
        const latestTask = latestTaskId ? DEFAULT_TASKS.find(t => t.id === latestTaskId) : null;

        // If user has completed tasks, show congratulations
        if (latestTask) {
          return (
            <Card className="bg-gradient-to-r from-secondary-50 to-accent-50 border-secondary-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-montserrat font-semibold text-base sm:text-lg text-neutral-800">
                      Congratulations! 🎉
                    </h3>
                    <p className="text-neutral-600">
                      You completed "{latestTask.title}" and earned {latestTask.xpReward} XP!
                    </p>
                  </div>
                </div>
                <Link to="/ai-readiness" className="w-full sm:w-auto">
                  <Button variant="secondary" icon={ArrowRight} iconPosition="right" className="w-full sm:w-auto">
                    View AI Readiness
                  </Button>
                </Link>
              </div>
            </Card>
          );
        }

        // If no tasks completed, show motivational banner
        return (
          <Card className="bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-montserrat font-semibold text-base sm:text-lg text-neutral-800">
                    Start Your AI Readiness Journey! 🚀
                  </h3>
                  <p className="text-neutral-600">
                    Complete tasks to earn XP and track your progress toward becoming AI-ready!
                  </p>
                </div>
              </div>
              <Link to="/ai-readiness" className="w-full sm:w-auto">
                <Button variant="primary" icon={ArrowRight} iconPosition="right" className="w-full sm:w-auto">
                  Get Started
                </Button>
              </Link>
            </div>
          </Card>
        );
      })()}
    </div>
  );
};

